import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  LegacyTransferTableValidateGridCoordinatesQuery,
  ServerResolutionResult,
  StringResolutionType,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {stubbableFunctions as GetGridSummaryQuery} from 'notebook/tables/typed-values/validateParentStorable';
import {InvalidPositionError, parseGridPosition, Position} from 'samples/SampleUtil';

import {splitCommaSeparatedString, StringResolutionError} from './TransferTableEditableRowsUtil';

/**
 * Custom resolution query that loads the parent plate or box and validates
 * the specified grid positions, returning an array of grid coordinate typed
 * values for downstream use.
 *
 * @param query the custom resolution query defined in StringResolutionConstants.ts
 * @returns an array typed value of grid coordinates
 */
export async function validateGridCoordinates(
  query: LegacyTransferTableValidateGridCoordinatesQuery
): Promise<ServerResolutionResult> {
  const {positionTypedValue, parentStorableTypedValue} = query;
  const parentStorableInfo = parentStorableTypedValue.storableSummaries[0];

  const formattedValue = formatTypedValue(positionTypedValue);
  if (formattedValue === '') {
    return {
      type: undefined,
      error: 'Position is required.',
      resolvedValue: undefined,
    };
  }

  const gridData = await GetGridSummaryQuery.doQuery(parentStorableInfo.id);
  const grid = gridData.storable;
  if (isInaccessible(grid)) {
    return {
      type: undefined,
      error: `Selected container is not available.`,
      resolvedValue: undefined,
    };
  }

  const gridSchema = grid.storableSchema as {width: number; height: number};
  const positionStrings = splitCommaSeparatedString(formattedValue);
  let positions: Array<Position> | null = null;
  try {
    positions = parsePositionStrings(positionStrings, gridSchema.height, gridSchema.width, grid.name);
  } catch (e) {
    if (e instanceof StringResolutionError) {
      return {
        type: undefined,
        error: e.message,
        resolvedValue: undefined,
      };
    } else {
      throw e;
    }
  }

  return {
    type: StringResolutionType.CUSTOM,
    resolvedValue: {
      typedValue: {
        type: BaseTypedValueType.ARRAY,
        rawValue: positions.map((position) => ({
          type: BaseTypedValueType.GRID_COORDINATES,
          gridCoordinates: {
            columnIndex: position.columnIndex,
            rowIndex: position.rowIndex,
          },
        })),
      },
    },
    error: undefined,
  };
}

function parsePositionStrings(
  positionStrings: Array<string>,
  gridHeight: number,
  gridWidth: number,
  storableName: string
): Array<Position> {
  // oxlint-disable-next-line array-callback-return
  const positions = positionStrings.map((position) => {
    try {
      return parseGridPosition({
        position,
        gridHeight,
        gridWidth,
      });
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        throw new StringResolutionError(`${position} is not a valid position in ${storableName}.`);
      } else {
        throw e;
      }
    }
  });
  return positions;
}
